import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { useAuth } from "@/hooks/useAuth";
import { useTierFeatures } from "@/hooks/useTierFeatures";
import { useScanBalance } from "@/hooks/useScanBalance";
import { useBulkPending } from "@/hooks/useBulkPending";
import { supabase } from "@/integrations/supabase/client";
import { expandPickedFiles, makeLocalPhoto, uploadPhotos, type LocalPhoto, MAX_PHOTOS } from "@/lib/photos";
import { toast } from "@/lib/toast";

const MAX_ITEMS = 50;
const CATEGORIES = [
  "Jacket / Coat",
  "Dress",
  "Top / Blouse",
  "Trousers / Skirt",
  "Knitwear",
  "Shoes",
  "Bag",
  "Accessory",
] as const;
type Category = (typeof CATEGORIES)[number];
// Use theme tokens so the screen tracks the active palette. `--marigold`
// is the gold accent every theme ships; `--ink` is the dark surface.
const GOLD = "hsl(var(--marigold))";
const PLUM = "hsl(var(--ink))";
const CREAM = "hsl(var(--cream))";

interface Slot {
  id: string;
  photos: LocalPhoto[];
  details: string;
  category: Category | null;
}

export default function BulkVerify() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { features } = useTierFeatures();
  const { balance } = useScanBalance();
  const { active } = useBulkPending();
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [slots, setSlots] = useState<Slot[]>([{ id: crypto.randomUUID(), photos: [], details: "", category: null }]);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Ready = has at least one photo AND a category selected.
  const itemsWithPhotos = useMemo(
    () => slots.filter((s) => s.photos.length > 0 && !!s.category),
    [slots],
  );
  // Canonical remaining-scans figure from useScanBalance (free + credits +
  // monthly cap when the sub is active). Don't recompute locally
  // or you'll drift from the consume-scan gate.
  const totalRemaining = balance?.totalRemaining ?? 0;
  const notEnough = itemsWithPhotos.length > totalRemaining;

  if (!features.canBulkUpload) {
    return (
      <MobileFrame>
        <div className="paper-bg" style={{ minHeight: "100vh", padding: 24 }}>
          {/* Wrap the text in a solid card. Trapper Keeper's paper-bg has
              a busy rainbow-stripe overlay that makes any text sitting
              directly on it hard to read; a cream-background card lets
              the same text breathe across every theme. */}
          <div style={{
            background: "hsl(var(--cream))",
            border: "1px solid hsl(var(--ink))",
            padding: "20px 22px",
          }}>
            <h1 style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 26, margin: 0 }}>Bulk verify</h1>
            <p style={{ marginTop: 12, fontFamily: "'Fraunces', 'Cormorant Garamond', serif", fontSize: 16, margin: "12px 0 0" }}>
              Bulk upload is a Pro Seller feature. Verify up to 50 items at once and review them at your own pace.
            </p>
            <button onClick={() => nav("/settings?tab=subs")} style={{ marginTop: 16, background: PLUM, color: "hsl(var(--ink-fg))", border: `1px solid ${PLUM}`, padding: "10px 14px", cursor: "pointer", fontFamily: "monospace", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Upgrade to Pro Seller →
            </button>
          </div>
        </div>
      </MobileFrame>
    );
  }

  if (active) {
    return (
      <MobileFrame>
        <div className="paper-bg" style={{ minHeight: "100vh", padding: 24 }}>
          <div style={{
            background: "hsl(var(--cream))",
            border: "1px solid hsl(var(--ink))",
            padding: "20px 22px",
          }}>
            <h1 style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 26, margin: 0 }}>A bulk job is still running</h1>
            <p style={{ marginTop: 12, fontFamily: "'Fraunces', 'Cormorant Garamond', serif", fontSize: 16, margin: "12px 0 0" }}>
              Verifying {active.completed} of {active.total}… you can start another batch once this one finishes.
            </p>
            <button onClick={() => nav("/bulk/review")} style={{ marginTop: 16, background: PLUM, color: "hsl(var(--ink-fg))", border: `1px solid ${PLUM}`, padding: "10px 14px", cursor: "pointer", fontFamily: "monospace", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Open Bulk Review →
            </button>
          </div>
        </div>
      </MobileFrame>
    );
  }

  const addSlot = () => {
    if (slots.length >= MAX_ITEMS) return;
    setSlots((s) => [...s, { id: crypto.randomUUID(), photos: [], details: "", category: null }]);
  };
  const removeSlot = (id: string) => setSlots((s) => s.filter((x) => x.id !== id));
  const onPickPhotos = async (slotId: string, fl: FileList | null) => {
    if (!fl) return;
    const expanded = await expandPickedFiles(Array.from(fl));
    const made = await Promise.all(expanded.slice(0, MAX_PHOTOS).map(makeLocalPhoto));
    setSlots((s) => s.map((x) => x.id === slotId ? { ...x, photos: [...x.photos, ...made].slice(0, MAX_PHOTOS) } : x));
  };
  const removePhoto = (slotId: string, photoId: string) => {
    setSlots((s) => s.map((x) => x.id === slotId
      ? { ...x, photos: x.photos.filter((p) => p.id !== photoId) }
      : x));
  };
  const setCategory = (slotId: string, cat: Category) => {
    setSlots((s) => s.map((x) => x.id === slotId
      ? { ...x, category: x.category === cat ? null : cat }
      : x));
  };
  const setDetails = (slotId: string, v: string) => setSlots((s) => s.map((x) => x.id === slotId ? { ...x, details: v.slice(0, 400) } : x));

  const submit = async () => {
    if (!user || itemsWithPhotos.length === 0) return;
    setSubmitting(true);
    try {
      // 1. Spend scans first.
      const { data: gate, error: gateErr } = await supabase.functions.invoke("consume-scan", {
        body: { count: itemsWithPhotos.length },
      });
      if (gateErr) throw new Error(gateErr.message);
      if (!gate?.ok) {
        toast.error("Not enough scans for this batch", { description: "Top up or upgrade to continue." });
        nav("/settings?tab=subs");
        return;
      }
      // 2. Upload all photos in parallel.
      const sessionId = `bulk-${Date.now()}`;
      const uploadedPerItem = await Promise.all(
        itemsWithPhotos.map((slot, idx) => uploadPhotos(user.id, `${sessionId}/${idx}`, slot.photos)),
      );
      // 3. Create job + items.
      const { data: job, error: jobErr } = await supabase
        .from("bulk_verification_jobs")
        .insert({ user_id: user.id, total_items: itemsWithPhotos.length, status: "processing" })
        .select("id").single();
      if (jobErr || !job) throw jobErr || new Error("Could not create job");
      const itemsPayload = itemsWithPhotos.map((slot, idx) => ({
        job_id: job.id,
        user_id: user.id,
        photos: uploadedPerItem[idx],
        extra_details: [
          slot.category ? `Category: ${slot.category}.` : null,
          slot.details ? `Additional details: ${slot.details}` : null,
        ].filter(Boolean).join(" ") || null,
        position: idx,
      }));
      const { error: itemsErr } = await supabase.from("bulk_verification_items").insert(itemsPayload);
      if (itemsErr) throw itemsErr;
      // 4. Kick off the worker (fire & forget — cron is the safety net).
      supabase.functions.invoke("process-bulk-queue", { body: {} })
        .catch((e) => console.warn("process-bulk-queue trigger failed (cron will retry):", e));
      toast.success("Batch submitted", { description: "We'll process them in the background." });
      nav("/bulk/review");
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : "Submission failed");
    } finally {
      setSubmitting(false);
      setConfirming(false);
    }
  };

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", padding: "24px 20px 120px" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <h1 style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 26, margin: 0 }}>Bulk verify</h1>
          <button onClick={() => nav(-1)} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "monospace", fontSize: 11 }}>Close</button>
        </div>
        <div style={{ marginTop: 4, fontFamily: "'Fraunces', 'Cormorant Garamond', serif", color: "hsl(var(--ink))", fontSize: 13 }}>
          {itemsWithPhotos.length} item{itemsWithPhotos.length === 1 ? "" : "s"} · {itemsWithPhotos.length} of {totalRemaining} scan{totalRemaining === 1 ? "" : "s"} will be used
        </div>

        <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 12 }}>
          {slots.map((slot, i) => (
            <div key={slot.id} style={{ border: "1px dashed hsl(var(--ink))", padding: 12, background: "hsl(var(--paper-3, var(--cream)))" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div className="caps" style={{ fontSize: 10, letterSpacing: "0.12em" }}>Item {i + 1}</div>
                {slots.length > 1 && (
                  <button onClick={() => removeSlot(slot.id)} className="text-secondary-foreground" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, fontFamily: "monospace" }}>Remove</button>
                )}
              </div>
              {/* Category selector — single-tap pills, horizontal scroll. */}
              <div style={{
                marginTop: 8, display: "flex", gap: 6, overflowX: "auto",
                flexWrap: "nowrap", paddingBottom: 4,
              }}>
                {CATEGORIES.map((c) => {
                  const active = slot.category === c;
                  return (
                    <button
                      key={c}
                      onClick={() => setCategory(slot.id, c)}
                      aria-pressed={active}
                      style={{
                        flexShrink: 0, height: 32, padding: "0 12px",
                        border: "1px solid hsl(var(--ink))",
                        background: active ? "hsl(var(--ink))" : "transparent",
                        color: active ? "hsl(var(--cream))" : "hsl(var(--ink))",
                        fontFamily: "monospace", fontSize: 10,
                        letterSpacing: "0.08em", textTransform: "uppercase",
                        whiteSpace: "nowrap", cursor: "pointer",
                      }}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
              {/* Photo row — up to 5 squares, horizontal scroll. */}
              <div style={{
                marginTop: 8, display: "flex", gap: 6, overflowX: "auto",
                flexWrap: "nowrap", paddingBottom: 4,
              }}>
                {slot.photos.map((p) => (
                  <div key={p.id} style={{ position: "relative", flexShrink: 0, width: 56, height: 56 }}>
                    <img src={p.previewUrl} alt="" style={{ width: 56, height: 56, objectFit: "cover", border: "1px solid hsl(var(--ink))" }} />
                    <button
                      onClick={() => removePhoto(slot.id, p.id)}
                      aria-label="Remove photo"
                      style={{
                        position: "absolute", top: -6, right: -6, width: 18, height: 18,
                        border: "1px solid hsl(var(--ink))", background: "hsl(var(--cream))",
                        color: "hsl(var(--ink))", cursor: "pointer", padding: 0,
                        fontSize: 12, lineHeight: 1, display: "flex",
                        alignItems: "center", justifyContent: "center",
                      }}
                    >×</button>
                  </div>
                ))}
                {slot.photos.length < MAX_PHOTOS && (
                  <button
                    onClick={() => fileRefs.current[slot.id]?.click()}
                    style={{ flexShrink: 0, width: 56, height: 56, border: "1px dashed hsl(var(--ink))", background: "transparent", cursor: "pointer", fontSize: 20 }}
                  >+</button>
                )}
                <input
                  ref={(el) => { fileRefs.current[slot.id] = el; }}
                  type="file" accept="image/*" multiple style={{ display: "none" }}
                  onChange={(e) => { void onPickPhotos(slot.id, e.target.files); e.target.value = ""; }}
                />
              </div>
              {/* Photo count + assessment level indicator */}
              <div className="text-secondary-foreground" style={{
                marginTop: 4, fontFamily: "monospace", fontSize: 10,
                letterSpacing: "0.04em",
              }}>
                {slot.photos.length === 0
                  ? "0 photos — add at least 1 to verify"
                  : `${slot.photos.length} photo${slot.photos.length === 1 ? "" : "s"} — ${
                      slot.photos.length === 1 ? "basic assessment"
                      : slot.photos.length === 2 ? "standard assessment"
                      : "full assessment"
                    }`}
              </div>
              <textarea
                value={slot.details}
                onChange={(e) => setDetails(slot.id, e.target.value)}
                placeholder="Optional details (brand spotted, condition…)"
                style={{ marginTop: 8, width: "100%", minHeight: 44, padding: 8, fontFamily: "'Fraunces', 'Cormorant Garamond', serif", fontSize: 14, border: "1px solid hsl(var(--ink))", background: "hsl(var(--cream))" }}
              />
            </div>
          ))}
        </div>

        <button
          onClick={addSlot}
          disabled={slots.length >= MAX_ITEMS}
          style={{ marginTop: 12, width: "100%", padding: "10px", border: "1px dashed hsl(var(--ink))", background: "transparent", cursor: "pointer", fontFamily: "monospace", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}
        >
          + Add another item ({slots.length}/{MAX_ITEMS})
        </button>

        <button
          onClick={() => setConfirming(true)}
          disabled={itemsWithPhotos.length === 0 || submitting || notEnough}
          style={{ marginTop: 16, width: "100%", padding: "12px", background: PLUM, color: "hsl(var(--ink-fg))", border: `1px solid ${PLUM}`, cursor: "pointer", fontFamily: "monospace", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", opacity: itemsWithPhotos.length === 0 || notEnough ? 0.4 : 1 }}
        >
          {notEnough ? `Only ${totalRemaining} scan${totalRemaining === 1 ? "" : "s"} left` : `Verify ${itemsWithPhotos.length} item${itemsWithPhotos.length === 1 ? "" : "s"}`}
        </button>
      </div>

      {confirming && (
        <div role="dialog" aria-modal="true" onClick={() => !submitting && setConfirming(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: PLUM, color: "hsl(var(--ink-fg))", padding: 22, maxWidth: 380, width: "100%", border: `1px solid hsl(var(--ink))` }}>
            <h2 style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', serif", color: "hsl(var(--ink-fg))", fontSize: 22, margin: 0 }}>
              Verify {itemsWithPhotos.length} items?
            </h2>
            <p style={{ marginTop: 10, fontFamily: "'Fraunces', 'Cormorant Garamond', serif", fontSize: 15, color: "hsl(var(--ink-fg))" }}>
              This will use {itemsWithPhotos.length} scan{itemsWithPhotos.length === 1 ? "" : "s"} from your allowance.
              You have <strong>{totalRemaining}</strong> remaining — {Math.max(0, totalRemaining - itemsWithPhotos.length)} will be left after.
            </p>
            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <button onClick={() => setConfirming(false)} disabled={submitting} style={{ flex: 1, padding: 10, background: "transparent", color: "hsl(var(--ink-fg))", border: `1px solid hsl(var(--ink-fg))`, cursor: "pointer", fontFamily: "monospace", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>Cancel</button>
              <button onClick={submit} disabled={submitting} style={{ flex: 1, padding: 10, background: "hsl(var(--ink-fg))", color: PLUM, border: "none", cursor: "pointer", fontFamily: "monospace", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>{submitting ? "Starting…" : "Start verifying"}</button>
            </div>
          </div>
        </div>
      )}
    </MobileFrame>
  );
}